import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
  ReferenceLine,
  Label
} from 'recharts';
import CustomLegend from '../Legend';
import TooltipContent from '../TooltipContent';
import styled from 'styled-components';
import ReferenceLineLabel from './ReferenceLine';

const BarGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};

  .recharts-cartesian-axis-line {
    stroke: #e4e4e4;
  }

  tspan {
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_bold};
  }

  .recharts-label-list tspan {
    fill: #2b3d54;
    font-size: 11px;
    font-family: ${(props) => props.theme.fonts.sfcompact_bold};
  }

  .rc-tooltip {
    opacity: 1;
  }

  .rc-tooltip-inner {
    border-radius: 4px;
  }
`;

class BarGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hovering: false,
      direction: 'top'
    };
  }

  formatData = () => {
    const { data, labels } = this.props;
    const aggregatedData = [];
    data.forEach((set, indexSet) => {
      set.forEach((item, index) => {
        const aggregatedItem = {
          ...item,
          [labels[indexSet]]: item.value.value ? item.value.value : item.value
        };

        if (aggregatedData[index]) {
          aggregatedData[index] = {
            ...aggregatedData[index],
            ...aggregatedItem
          };
        } else {
          aggregatedData.push(aggregatedItem);
        }
      });
    });
    return aggregatedData;
  };

  handleMouverOver(props, data, set) {
    let indexData = set.findIndex((item) => item.name === props.name);
    let coordinates = {
      x: props.x - 191 + props.width / 2,
      y: props.background.height - props.height - 80 - data.length * 20
    };
    let limit = 1;
    if (set.length > 9) {
      limit = parseInt(set.length / 6);
    }
    if (indexData >= set.length - limit && set.length !== 1) {
      coordinates.x = coordinates.x - 250;
      coordinates.y = coordinates.y + 100;
    }
    if (indexData <= limit && set.length > 5 && set.length !== 1) {
      coordinates.x = coordinates.x + 250;
      coordinates.y = coordinates.y + 50;
    }

    let direction = 'top';
    if (indexData >= set.length - limit && set.length !== 1) {
      direction = 'left';
    } else if (indexData <= limit && set.length > 5 && set.length !== 1) {
      direction = 'right';
    }

    this.setState({
      direction,
      hovering: props.name,
      coordinates: coordinates
    });
  }

  handleMouseOut() {
    const { cancelControlledHover } = this.props;
    this.setState({ hovering: false });
    if (cancelControlledHover) {
      cancelControlledHover();
    }
  }

  renderBars(data, singleBar) {
    let { hovering } = this.state;
    const {
      offset,
      angle,
      position,
      isStacked,
      hoverAsBorder,
      firstBarColors
    } = this.props;
    const {
      labels,
      showLabel,
      renderLabel,
      tooltip,
      customColors,
      isAnimationActive
    } = this.props;
    const colors = customColors
      ? customColors
      : [
          '#e25d34',
          'rgba(43, 61, 84,1)',
          'rgba(43, 61, 84,0.85)',
          'rgba(43, 61, 84,0.7)',
          'rgba(43, 61, 84,0.5)',
          'rgba(43, 61, 84,0.35)',
          'rgba(43, 61, 84,0.2)'
        ];
    if (tooltip === false) {
      hovering = false;
    }
    return data.map((set, index) => (
      <Bar
        isAnimationActive={isAnimationActive}
        stackId={isStacked ? '1' : undefined}
        key={`bar-${index}`}
        onMouseOver={(props) => this.handleMouverOver(props, data, set)}
        onMouseOut={() => this.handleMouseOut()}
        dataKey={labels[index] || index}
        fill={singleBar ? null : colors[index]}
        barSize={30}
      >
        {showLabel && (
          <LabelList
            style={{ pointerEvents: 'none', color: '#2b3d54' }}
            dataKey={labels[index]}
            formatter={(value) => (value ? Number(value).toFixed(0) : null)}
            content={renderLabel ? (props) => renderLabel(props) : null}
            position={position ? position : 'top'}
            offset={offset ? offset : 5}
            angle={angle ? angle : 0}
          />
        )}
        {set.map((entry, indexSet) => {
          return (
            <Cell
              key={`cell-${index}`}
              stroke={
                hoverAsBorder && hovering && hovering === entry.name
                  ? '#e25d34'
                  : undefined
              }
              strokeWidth={
                hoverAsBorder && hovering && hovering === entry.name
                  ? 3
                  : undefined
              }
              fill={
                firstBarColors && indexSet === 0
                  ? firstBarColors[index]
                  : singleBar
                    ? colors[indexSet]
                    : colors[index]
              }
              opacity={
                hoverAsBorder || hovering === false || hovering === entry.name
                  ? 1
                  : 0.3
              }
            />
          );
        })}
      </Bar>
    ));
  }

  renderReferenceLine = () => {
    const { referenceLine } = this.props;
    return (
      <ReferenceLine y={referenceLine.value} strokeWidth={3} stroke="#e25d34">
        <Label
          value={referenceLine.label}
          content={<ReferenceLineLabel />}
          position="right"
        />
      </ReferenceLine>
    );
  };

  helperColorArrow() {
    const { colorArrow } = this.props;
    const { direction } = this.state;
    if (colorArrow) {
      const style = {};
      Object.keys(colorArrow).map((key) => {
        style[`border${key}Color`] =
          direction === key.toLowerCase() ? colorArrow[key] : 'transparent';
      });
      return style;
    }
  }

  getPosition() {
    const { coordinates, direction } = this.state;
    const { tooltipOffset, tooltipOffsetRight, tooltipOffsetLeft } = this.props;
    if (tooltipOffsetRight && direction === 'right') {
      return {
        x: coordinates.x + tooltipOffsetRight.x,
        y: coordinates.y + tooltipOffsetRight.y
      };
    }
    if (tooltipOffsetLeft && direction === 'left') {
      return {
        x: coordinates.x + tooltipOffsetLeft.x,
        y: coordinates.y + tooltipOffsetLeft.y
      };
    }
    if (tooltipOffset && direction === 'top') {
      return {
        x: coordinates.x + tooltipOffset.x,
        y: coordinates.y + tooltipOffset.y
      };
    }
    return coordinates;
  }

  renderHovering = (
    singleBar,
    labels,
    currency,
    tooltipContent,
    hoverData,
    tooltip,
    emptyTooltip
  ) => {
    let { direction } = this.state;
    const { renderTooltip } = this.props;
    const position = this.getPosition();

    return (
      <Tooltip
        content={
          emptyTooltip ? (
            () => {}
          ) : (
            <div
              className={`rc-tooltip overlayTooltip rc-tooltip-placement-${direction} rc-tooltip-inner`}
            >
              <div className="rc-tooltip-content">
                <div
                  className="rc-tooltip-arrow"
                  style={this.helperColorArrow()}
                />
                <div className="rc-tooltip-inner" role="tooltip">
                  {renderTooltip ? (
                    renderTooltip(hoverData)
                  ) : (
                    <TooltipContent
                      singleBar={singleBar}
                      labels={labels}
                      currency={currency}
                      {...tooltipContent}
                      data={hoverData}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        }
        cursor={false}
        position={position}
        {...tooltip}
      />
    );
  };

  getHoverData = () => {
    const { hovering } = this.state;
    const { data } = this.props;
    let hoverData = [];
    if (hovering) {
      data.forEach((set) => {
        hoverData.push(set.find((item) => item.name === hovering));
      });
    }
    return hoverData;
  };

  render() {
    const {
      data,
      labels,
      singleBar,
      currency,
      tooltip,
      tooltipContent,
      height,
      emptyTooltip,
      customLegendProps,
      customColors,
      referenceLine,
      margin,
      ...otherProps
    } = this.props;
    const colors = customColors
      ? customColors
      : [
          '#e25d34',
          'rgba(43, 61, 84,1)',
          'rgba(43, 61, 84,0.85)',
          'rgba(43, 61, 84,0.7)',
          'rgba(43, 61, 84,0.5)',
          'rgba(43, 61, 84,0.35)',
          'rgba(43, 61, 84,0.2)'
        ];
    const formattedData = this.formatData();
    const hoverData = this.getHoverData();
    let { hovering } = this.state;
    if (tooltip === false) {
      hovering = false;
    }

    return (
      <BarGraphContainer id={this.props.id}>
        <ResponsiveContainer width="100%" height={height ? height : 600}>
          <BarChart
            height={height ? height : 600}
            data={formattedData}
            barCategoryGap="30%"
            barGap="3%"
            margin={margin ? margin : undefined}
            onMouseOut={() => this.setState({ hovering: false })}
          >
            <CartesianGrid vertical={false} stroke="#e4e4e4" />
            <XAxis
              dataKey="name"
              tickLine={false}
              allowDecimals={false}
              {...otherProps.xAxisProps}
            />
            <YAxis
              allowDecimals={false}
              width={100}
              tickLine={false}
              tickFormatter={(value) =>
                currency
                  ? value.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: currency,
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1
                    })
                  : value
              }
              {...otherProps.yAxisProps}
            />
            {referenceLine && this.renderReferenceLine(referenceLine)}
            {hovering &&
              this.renderHovering(
                singleBar,
                labels,
                currency,
                tooltipContent,
                hoverData,
                tooltip,
                emptyTooltip
              )}
            <Legend
              content={(props) => (
                <CustomLegend {...props} {...customLegendProps} />
              )}
              colors={colors}
              labels={labels}
            />
            {this.renderBars(data, singleBar, hovering)}
          </BarChart>
        </ResponsiveContainer>
      </BarGraphContainer>
    );
  }
}

export default BarGraph;
